/**
 * Preferred language after sign-in (v4.8.389).
 *
 * The first list follows the country on the user's KYC, for every country in the
 * country table. South Africa and India use the languages stored against that
 * country. Every other country uses the catalog languages spoken there.
 * Search and the full list stay available, so a person can still choose another language.
 * The choice is stored on users.pref_language.
 * Dashboard UI catalogs exist for a smaller set, so language_preference is updated only for those codes.
 */

export const UI_LANGUAGE_CODES = new Set(['en', 'fr', 'pt', 'es', 'ar', 'sw', 'zu', 'hi', 'zh'])

// Catalog languages for each KYC country other than South Africa and India.
// Those two countries are read from supported_languages.country_code.
// A country whose own language is not in the 45-language catalog is offered English.
// Search still opens the full catalog.
export const KYC_COUNTRY_LANGUAGES = {
  ABW: ['nl'], AFG: ['en'], AGO: ['pt'], ALB: ['en'], AND: ['es', 'fr'], ARE: ['ar'],
  ARG: ['es'], ARM: ['en'], ATG: ['en'], AUS: ['en'], AUT: ['de'], AZE: ['en'],
  BEL: ['nl', 'fr', 'de'], BEN: ['fr'], BES: ['nl'], BFA: ['fr'], BGD: ['bn'],
  BGR: ['en'], BHR: ['ar'], BHS: ['en'], BIH: ['en'], BLR: ['ru'], BLZ: ['en', 'es'],
  BOL: ['es'], BRA: ['pt'], BRB: ['en'], BRN: ['ms'], BTN: ['en'], BWA: ['en', 'tn'],
  BDI: ['fr', 'en'], CAF: ['fr'], CAN: ['en', 'fr'], CHE: ['de', 'fr', 'it'], CHL: ['es'],
  CHN: ['zh'], CIV: ['fr'], CMR: ['fr', 'en'], COD: ['fr'], COG: ['fr'], COL: ['es'],
  COM: ['ar', 'fr'], CPV: ['pt'], CRI: ['es'], CUB: ['es'], CUW: ['nl'], CYM: ['en'],
  CYP: ['el', 'en'], CZE: ['cs'], DEU: ['de'], DJI: ['fr', 'ar'], DMA: ['en'], DNK: ['en'],
  DOM: ['es'], DZA: ['ar', 'fr'], ECU: ['es'], EGY: ['ar'], ERI: ['ar', 'en'], ESP: ['es'],
  EST: ['en'], ETH: ['am'], FIN: ['en'], FJI: ['en'], FRA: ['fr'], FSM: ['en'],
  GAB: ['fr'], GBR: ['en'], GEO: ['en'], GHA: ['en'], GIN: ['fr'], GMB: ['en'],
  GNB: ['pt'], GNQ: ['es', 'fr', 'pt'], GRC: ['el'], GRD: ['en'], GTM: ['es'],
  GUY: ['en'], HND: ['es'], HRV: ['en'], HTI: ['fr'], HUN: ['en'], IDN: ['id'],
  IRL: ['en'], IRN: ['en'], IRQ: ['ar'], ISL: ['en'], ISR: ['en'], ITA: ['it'],
  JAM: ['en'], JOR: ['ar'], JPN: ['en'], KAZ: ['ru'], KEN: ['sw', 'en'], KGZ: ['ru'],
  KHM: ['en'], KIR: ['en'], KNA: ['en'], KOR: ['en'], KWT: ['ar'], LAO: ['en'],
  LBN: ['ar', 'fr'], LBR: ['en'], LBY: ['ar'], LCA: ['en'], LIE: ['de'], LKA: ['ta', 'en'],
  LSO: ['en', 'st'], LTU: ['en'], LUX: ['fr', 'de'], LVA: ['en'], MAF: ['fr'], MAR: ['ar', 'fr'],
  MCO: ['fr'], MDA: ['ro'], MDG: ['fr'], MDV: ['en'], MEX: ['es'], MHL: ['en'],
  MKD: ['en'], MLI: ['fr'], MLT: ['en'], MMR: ['en'], MNE: ['en'], MNG: ['en'],
  MOZ: ['pt'], MRT: ['ar', 'fr'], MUS: ['en', 'fr'], MWI: ['en'], MYS: ['ms'],
  NAM: ['en', 'af'], NER: ['fr', 'ha'], NGA: ['en', 'ha', 'yo'], NIC: ['es'], NLD: ['nl'],
  NOR: ['en'], NPL: ['en'], NRU: ['en'], NZL: ['en'], OMN: ['ar'], PAK: ['ur', 'en'],
  PAN: ['es'], PER: ['es'], PHL: ['tl', 'en'], PLW: ['en'], PNG: ['en'], POL: ['pl'],
  PRK: ['en'], PRT: ['pt'], PRY: ['es'], PSE: ['ar'], QAT: ['ar'], ROU: ['ro'],
  RUS: ['ru'], RWA: ['rw', 'fr', 'en'], SAU: ['ar'], SDN: ['ar', 'en'], SEN: ['fr'],
  SGP: ['en', 'zh', 'ms', 'ta'], SLB: ['en'], SLE: ['en'], SLV: ['es'], SMR: ['it'],
  SOM: ['ar'], SRB: ['en'], SSD: ['en', 'ar'], STP: ['pt'], SUR: ['nl'], SVK: ['sk'],
  SVN: ['en'], SWE: ['en'], SWZ: ['en', 'ss'], SXM: ['nl'], SYC: ['en', 'fr'], SYR: ['ar'],
  TCA: ['en'], TCD: ['fr', 'ar'], TGO: ['fr'], THA: ['th'], TJK: ['en'], TKM: ['en'], TLS: ['pt'],
  TON: ['en'], TTO: ['en'], TUN: ['ar', 'fr'], TUR: ['en'], TUV: ['en'], TWN: ['zh'],
  TZA: ['sw', 'en'], UGA: ['en', 'sw'], UKR: ['en'], URY: ['es'], USA: ['en'],
  UZB: ['en'], VAT: ['it'], VCT: ['en'], VEN: ['es'], VNM: ['vi'], VUT: ['en'],
  WSM: ['en'], XKX: ['en'], YEM: ['ar'], ZMB: ['en'], ZWE: ['en'],
}

export function normalizeLanguageCode(code) {
  const value = String(code || '').trim().toLowerCase()
  if (!/^[a-z]{2,10}$/.test(value)) return null
  return value
}

export function normalizeCountryCode(code) {
  const value = String(code || '').trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(value)) return null
  return value
}

export function languagesForKycCountry(catalog, countryCode) {
  const code = normalizeCountryCode(countryCode)
  if (!code) return []
  const rows = (catalog || []).filter((row) => row && row.is_active !== false)
  if (code === 'ZAF' || code === 'IND') {
    return rows
      .filter((row) => row.country_code === code)
      .slice()
      .sort((a, b) => Number(a.sort_order) - Number(b.sort_order))
  }
  const wanted = KYC_COUNTRY_LANGUAGES[code]
  const byCode = new Map(rows.map((row) => [String(row.code || '').toLowerCase(), row]))
  if (!wanted || wanted.length === 0) {
    const english = byCode.get('en')
    return english ? [english] : []
  }
  return wanted.map((language) => byCode.get(language)).filter(Boolean)
}

export function searchLanguages(catalog, query) {
  const q = String(query || '').trim().toLowerCase()
  const rows = (catalog || []).filter((row) => row && row.is_active !== false)
  if (!q) return rows.slice().sort((a, b) => Number(a.sort_order) - Number(b.sort_order))
  return rows
    .filter((row) => {
      const name = String(row.name || '').toLowerCase()
      const code = String(row.code || '').toLowerCase()
      const region = String(row.region_group || '').toLowerCase()
      return name.includes(q) || code.includes(q) || region.includes(q)
    })
    .sort((a, b) => Number(a.sort_order) - Number(b.sort_order))
}

export function needsLanguageChoice(prefLanguage) {
  return normalizeLanguageCode(prefLanguage) == null
}

export function planPrefLanguageSave(code, catalog) {
  const normalized = normalizeLanguageCode(code)
  if (!normalized) {
    return { ok: false, status: 400, error: 'Choose a language from the list.' }
  }
  const row = (catalog || []).find((item) => item && item.code === normalized && item.is_active !== false)
  if (!row) {
    return { ok: false, status: 400, error: 'That language is not available.' }
  }
  const updates = { pref_language: normalized }
  if (UI_LANGUAGE_CODES.has(normalized)) updates.language_preference = normalized
  return { ok: true, updates, language: row }
}
